/**
 * useScopeFitSuggestions — Round 78 (A-C-09)
 *
 * Read-only, advisory cross-location fit suggestions. Built on top of
 * R74 talent analytics data via the `get_scope_fit_suggestions` RPC.
 *
 * STRICTLY:
 *   - deterministic (boolean signals only — no composite score)
 *   - explainable (server returns the human-readable reason summary)
 *   - read-only (no scheduling / no assignment / no Connect mutation)
 *
 * Gated by `canViewInScopeTalent` from useEnterpriseScope.
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useEnterpriseScope } from './useEnterpriseScope';
import type { InScopeScopeType } from './useInScopeTalent';

export type FitConfidence = 'low' | 'medium' | 'high';

export type FitSignalKey =
  | 'high_efficiency'
  | 'low_comeback_rate'
  | 'strong_diagnostic_accuracy'
  | 'high_job_volume'
  | 'recent_training_activity';

export type FitSignals = Record<FitSignalKey, boolean>;

export interface ScopeFitSuggestion {
  user_id: string;
  display_name: string;
  current_shop_id: string;
  current_shop_name: string;
  role: string;
  technician_type_title: string;
  jobs_completed: number;
  diagnostic_sample_size: number;
  fit_signals: FitSignals;
  signal_count: number;
  confidence_level: FitConfidence;
  reason_summary: string;
  last_computed_at: string;
}

export interface ScopeFitFilters {
  shop_ids?: string[];
  role?: string;
  search?: string;
  confidence?: FitConfidence;
  signals?: FitSignalKey[];
}

interface UseScopeFitSuggestionsArgs {
  scopeType: InScopeScopeType | null;
  scopeId: string | null;
  filters?: ScopeFitFilters;
}

export function useScopeFitSuggestions({
  scopeType,
  scopeId,
  filters,
}: UseScopeFitSuggestionsArgs) {
  const { canViewInScopeTalent } = useEnterpriseScope();
  const enabled = canViewInScopeTalent && !!scopeType && !!scopeId;

  // Only forward non-empty filter fields so missing keys mean "no filter"
  const cleanFilters = filters
    ? Object.fromEntries(
        Object.entries(filters).filter(([, v]) =>
          Array.isArray(v) ? v.length > 0 : v !== undefined && v !== '' && v !== null,
        ),
      )
    : {};

  const query = useQuery({
    queryKey: ['scope-fit-suggestions', scopeType, scopeId, cleanFilters],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_scope_fit_suggestions', {
        p_scope_type: scopeType!,
        p_scope_id: scopeId!,
        p_filters: cleanFilters as any,
      });
      if (error) throw error;
      return (data ?? []) as ScopeFitSuggestion[];
    },
    enabled,
  });

  return {
    suggestions: query.data ?? [],
    isLoading: query.isLoading,
    error: (query.error as Error | null) ?? null,
  };
}
